import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {
  createMailTemplate,
  deleteMailTemplate,
  loadMailTemplateParams,
  loadMailTemplates,
  updateMailTemplate,
} from './Api'
import {IMailTemplateModel, IMailTemplateParam} from './MailTemplateModel'
import {MailTemplateFormModal} from './MailTemplateFormModal'
import {MailTemplateTable} from './MailTemplateTable'

const MailTemplatePage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [templates, setTemplates] = useState<IMailTemplateModel[]>([])
  const [params, setParams] = useState<IMailTemplateParam[]>([])
  const [paramsLoading, setParamsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<IMailTemplateModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<IMailTemplateModel | null>(null)

  const fetchTemplates = useCallback(async (page: number) => {
    setLoading(true)
    try {
      const payload = (await loadMailTemplates(page)).data.payload
      setTemplates(payload.content)
      setTotalPages(payload.totalPages)
    } catch (error) {
      showErrorToast('Mail şablonları yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTemplates(0)
  }, [fetchTemplates])

  // Parametre listesi değişmediği için bir kez yüklenir
  useEffect(() => {
    setParamsLoading(true)
    loadMailTemplateParams()
      .then(({data}) => setParams(data.payload || []))
      .catch(() => showErrorToast('Parametre listesi yüklenemedi'))
      .finally(() => setParamsLoading(false))
  }, [])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchTemplates(newPage)
      }
    },
    [totalPages, fetchTemplates]
  )

  const handleCreateClick = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleEdit = useCallback((template: IMailTemplateModel) => {
    setEditingItem(template)
    setShowForm(true)
  }, [])

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleSubmit = async (template: IMailTemplateModel) => {
    setSaving(true)
    try {
      if (editingItem?.id) {
        await updateMailTemplate({...template, id: editingItem.id})
        showSuccessToast('Mail şablonu güncellendi')
        await fetchTemplates(currentPage)
      } else {
        await createMailTemplate({...template, id: undefined})
        showSuccessToast('Mail şablonu oluşturuldu')
        setCurrentPage(0)
        await fetchTemplates(0)
      }
      handleCloseForm()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Bir hata oluştu'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteMailTemplate(deletingItem.id)
      showSuccessToast('Mail şablonu silindi')
      await fetchTemplates(currentPage)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Mail şablonu silinirken hata oluştu'))
    } finally {
      setDeletingItem(null)
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Mail Şablonu</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Mail Şablonları</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Süreç adımlarında ve soru koşullarında adaylara otomatik gönderilen mailler
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={handleCreateClick}>
              <i className='bi bi-plus-lg fs-5'></i>
              Yeni Kayıt
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <MailTemplateTable
            data={templates}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <MailTemplateFormModal
        show={showForm}
        editingItem={editingItem}
        params={params}
        paramsLoading={paramsLoading}
        loading={saving}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`"${deletingItem?.name ?? ''}" şablonunu silmek istediğinize emin misiniz?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  )
}

export {MailTemplatePage}
